import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { loucaApi, buildImageUrl } from '../services/loucaApi';
import { useAuth } from '../contexts/AuthContext';
import EditableField from '../components/EditableField';
import ImageModal from '../components/ImageModal';
import { normalizeAnalysisWorkflow, getWorkflowBadge } from '../utils/analysisWorkflow';

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const InspectionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(location.state?.status || 'processing');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    loadInspection();
  }, [id]);

  useEffect(() => {
    const lastError = sessionStorage.getItem('louca_last_upload_error');
    if (lastError) {
      setUploadError(lastError);
      sessionStorage.removeItem('louca_last_upload_error');
    }
  }, []);

  useEffect(() => {
    if (status !== 'processing') {
      return undefined;
    }
    const stopPolling = startPolling();
    return stopPolling;
  }, [status]);

  const loadInspection = async () => {
    try {
      const response = await loucaApi.getInspection(id);
      if (response.status === 'success') {
        setInspection(response.data);
        setStatus(response.data.status);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading inspection:', err);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    const interval = setInterval(async () => {
      try {
        const response = await loucaApi.getInspectionStatus(id);
        setProgress(response.data.progress_percent || 0);
        setStatus(response.data.status);
        
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          clearInterval(interval);
          if (response.data.status === 'completed') {
            loadInspection();
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  };

  const handleFieldUpdate = async (field_id, old_value, new_value) => {
    try {
      await loucaApi.updateLine({
        inspection_id: id,
        field_id,
        old_value,
        new_value,
        author: 'driver',
        owner: { type: 'driver', id: user?.user_id || 'DRIVER' },
        timestamp: new Date().toISOString(),
      });
      
      loadInspection();
    } catch (err) {
      console.error('Update error:', err);
      alert('Erreur lors de la mise à jour');
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `${Number(value).toFixed(2)} €`;
  };

  const formatConfidence = (value) => {
    if (value === null || value === undefined) return '—';
    return `${Math.round(value * 100)}%`;
  };

  const workflowModel = useMemo(
    () => (inspection ? normalizeAnalysisWorkflow(inspection) : null),
    [inspection]
  );

  const workflowReport = workflowModel?.report;
  const hasStructuredReport = Boolean(workflowReport);
  const hasLegacyReport =
    !hasStructuredReport &&
    inspection?.report &&
    Object.keys(inspection.report).length > 0;

  const pipelineBadge = workflowModel ? getWorkflowBadge(workflowModel) : 'Rapport';

  const damages = workflowModel?.damages || [];
  const [fallbackOutlined, setFallbackOutlined] = useState([]);
  const [outlinedByDetectionId, setOutlinedByDetectionId] = useState({});
  
  const displayDamages = useMemo(() => {
    return damages.map((damage) => {
      const part = damage.part || damage.part_label || damage.part_id || 'Partie non identifiée';
      const type = (damage.type || damage.type_code || 'inconnu');
      let outlined = null;
      if (damage.outlined_image_url) {
        outlined = buildImageUrl(damage.outlined_image_url);
      } else if (damage.frame_url) {
        outlined = buildImageUrl(damage.frame_url);
      } else if (damage.detection_id) {
        // Prefer URL from results.json mapping when available
        if (outlinedByDetectionId[damage.detection_id]) {
          outlined = outlinedByDetectionId[damage.detection_id];
        } else {
        const pct = Math.round(((damage.confidence || 0) * 100));
        const label = String(type).replace(/\s+/g, '_');
        const file = `${damage.detection_id}_${label}_${pct}.jpg`;
        outlined = buildImageUrl(`/api/v0.2/uploads/${id}/results/predictions/${file}`);
        }
      }
      return {
        ...damage,
        part,
        type,
        outlined_image_full: outlined,
      };
    });
  }, [damages, id, outlinedByDetectionId]);

  const media = workflowModel?.media || { frames: [], outlined: [], extracted: [] };
  const processedFrames = media.frames || [];
  const outlinedImages = useMemo(() => {
    const base = ensureArray(media.outlined)
      .map((img) => {
        const url = buildImageUrl(img.url || img.path);
        if (!url) return null;
        return {
          ...img,
          url,
          filename: img.filename || img.damage_id || img.url || img.path,
        };
      })
      .filter(Boolean);
    if (base.length > 0) return base;

    const framesFallback = ensureArray(processedFrames)
      .map((frame) => {
        const url = buildImageUrl(frame.url || frame.path);
        if (!url) return null;
        return {
          ...frame,
          url,
          filename: frame.filename || frame.url || frame.path,
          type: frame.type || 'frame',
        };
      })
      .filter(Boolean);
    if (framesFallback.length > 0) return framesFallback;

    if (fallbackOutlined.length > 0) return fallbackOutlined;
    return [];
  }, [media, processedFrames, fallbackOutlined]);

  // Build outlined URLs from results.json when media.outlined is empty
  useEffect(() => {
    const buildFromResults = async () => {
      try {
        const reports = workflowModel?.report?.reports || [];
        const yolo = reports[0]?.yolo_results || {};
        const resultsFile = yolo.results_file;
        if (!resultsFile || outlinedImages.length > 0) return;
        // resultsFile like /app/uploads/{iid}/results/results.json
        const parts = String(resultsFile).split(`/app/uploads/${id}/`);
        if (parts.length !== 2) return;
        const rel = parts[1]; // results/results.json
        const resultsUrl = buildImageUrl(`/api/v0.2/uploads/${id}/${rel}`);
        const resp = await fetch(resultsUrl);
        if (!resp.ok) return;
        const data = await resp.json();
        const items = [];
        const map = {};
        for (const r of data.results || []) {
          const dets = r.detections || [];
          if (!dets.length) continue;
          const d = dets[0];
          const detId = d.detection_id || 'det_1';
          const label = String(d.class || 'unknown').replace(/\s+/g, '_');
          const pct = Math.round(((d.confidence || 0) * 100));
          const file = `${detId}_${label}_${pct}.jpg`;
          const url = buildImageUrl(`/api/v0.2/uploads/${id}/results/predictions/${file}`);
          items.push({ url, filename: file, type: 'outlined' });
          map[detId] = url;
        }
        if (items.length) setFallbackOutlined(items);
        if (Object.keys(map).length) setOutlinedByDetectionId(map);
      } catch (e) {
        // ignore silently but log to console for dev
        console.warn('Failed to build outlined images from results.json', e);
      }
    };
    buildFromResults();
  }, [id, workflowModel, outlinedImages.length]);

  const summary = workflowModel?.summary || {};
  const damageBreakdown = summary.damages_by_type || {};
  const totalDamagesCount = summary.total_damages ?? displayDamages.length;
  const totalCostData = workflowModel?.totalCost;
  const recommendations = workflowModel?.recommendations || [];
  const qa = workflowModel?.qa;
  const missingViews = workflowModel?.missingViews || [];
  const timelineSteps = workflowModel?.steps || [];

  const primaryVideo = inspection?.videos?.[0];
  const reportDisplayLabel = hasStructuredReport
    ? pipelineBadge
    : hasLegacyReport
      ? 'Rapport classique (flux historique)'
      : null;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </Layout>
    );
  }

  if (error || !inspection) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error || 'Inspection non trouvée'}</p>
          <button
            onClick={() => navigate('/inspections')}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg"
          >
            Retour
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-24 max-w-6xl mx-auto space-y-10">
        {uploadError && (
          <div className="bg-red-500/20 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg">
            {uploadError}
          </div>
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
          <h1 className="text-3xl font-bold text-white">Inspection #{id}</h1>
            <p className="text-sm text-slate-400">
              Workflow : {pipelineBadge}
            </p>
            {reportDisplayLabel && (
              <p className="mt-1 text-xs text-slate-500">
                Rapport affiché : {reportDisplayLabel}
              </p>
            )}
            {inspection.car_info?.license_plate && (
              <p className="mt-2 text-sm text-slate-300">
                Plaque :{' '}
                <span className="text-white font-semibold">
                  {inspection.car_info.license_plate}
                </span>
              </p>
            )}
            {primaryVideo && (
              <p className="mt-1 text-xs text-slate-500">
                Résolution vidéo :
                {' '}
                {primaryVideo.width && primaryVideo.height
                  ? `${primaryVideo.width}×${primaryVideo.height}`
                  : 'N/A'}
                {primaryVideo.fps ? ` • ${primaryVideo.fps} fps` : ''}
                {primaryVideo.duration_seconds ? ` • ${primaryVideo.duration_seconds}s` : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate(`/inspection/${id}/dev`)}
              className="px-4 py-2 bg-orange-500/20 border border-orange-500/50 text-orange-300 rounded-lg hover:bg-orange-500/30 transition"
            >
              Vue développeur
            </button>
          <button
            onClick={() => navigate('/inspections')}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
          >
            ← Retour
          </button>
          </div>
        </div>

        {inspection.comment && (
          <div className="bg-orange-500/10 border border-orange-500/40 rounded-xl px-5 py-4 text-sm text-orange-100">
            <p className="text-xs uppercase tracking-[0.3em] text-orange-300">Commentaire utilisateur</p>
            <p className="mt-2 text-orange-100">{inspection.comment}</p>
          </div>
        )}

        {status === 'processing' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-300">Analyse en cours...</span>
              <span className="text-orange-400 font-semibold">{progress}%</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-orange-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-6">
            <p className="text-red-300">L'analyse a échoué. Veuillez réessayer.</p>
          </div>
        )}

        {status === 'completed' && hasStructuredReport && (
          <div className="space-y-10">
            {timelineSteps.length > 0 && (
              <section className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Workflow</p>
                <div className="mt-4 grid gap-4 md:grid-cols-3 sm:grid-cols-2">
                  {timelineSteps.map((step) => (
                    <div
                      key={step.id}
                      className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 space-y-2"
                    >
                      <p className="text-sm font-semibold text-white">{step.label || step.id}</p>
                      {step.status && (
                        <p className="text-xs text-slate-400">Statut : {step.status}</p>
                      )}
                      {typeof step.media_count === 'number' && (
                        <p className="text-xs text-slate-400">Images : {step.media_count}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="grid gap-6 lg:grid-cols-2">
              <div className="bg-gradient-to-br from-orange-500/20 via-slate-800 to-slate-900 border border-orange-500/40 rounded-2xl overflow-hidden shadow-lg">
                <div className="bg-slate-900/60 px-6 py-4 border-b border-orange-500/30">
                  <p className="text-sm uppercase tracking-[0.3em] text-orange-300">Aperçu média</p>
                  <h2 className="text-2xl font-semibold text-white mt-1">{reportDisplayLabel}</h2>
                </div>
                {outlinedImages.length > 0 ? (
                  <button
                    onClick={() => setActiveImage(outlinedImages[0].url)}
                    className="relative group"
                  >
                    <img
                      src={outlinedImages[0].url}
                      alt={outlinedImages[0].filename}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-sm">
                      Cliquez pour agrandir
                    </span>
                  </button>
                ) : (
                  <div className="flex items-center justify-center h-64 bg-slate-900 text-slate-500 text-sm">
                    Aucune image détourée disponible
                  </div>
                )}
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-6 shadow-lg">
                <div>
                  <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Résumé</p>
                  <h2 className="text-3xl font-bold text-white mt-2">
                    {totalDamagesCount} dommages détectés
                  </h2>
                </div>
                {totalCostData && (
                  <div className="bg-orange-500/10 border border-orange-500/40 rounded-xl p-4">
                    <p className="text-sm text-orange-300 uppercase tracking-widest">Coût total estimé</p>
                    <p className="text-3xl font-semibold text-orange-200 mt-1">
                      {formatCurrency(
                        typeof totalCostData === 'number'
                          ? totalCostData
                          : totalCostData.total
                      )}
                    </p>
                    {typeof totalCostData === 'object' && totalCostData !== null && (
                      <div className="mt-3 text-xs text-slate-300 space-y-1">
                        {totalCostData.parts !== undefined && (
                          <p>Pièces : {formatCurrency(totalCostData.parts)}</p>
                        )}
                        {totalCostData.labor !== undefined && (
                          <p>Main d'œuvre : {formatCurrency(totalCostData.labor)}</p>
                        )}
                        {totalCostData.currency && (
                          <p>Devise : {totalCostData.currency}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {Object.keys(damageBreakdown).length > 0 && (
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Répartition par type</p>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(damageBreakdown).map(([type, count]) => (
                        <span
                          key={type}
                          className="px-3 py-1 rounded-full border border-orange-500/40 text-orange-200 bg-orange-500/10 text-sm"
                        >
                          {type} • {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {recommendations.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Recommandations</p>
                    <ul className="space-y-2 text-sm text-slate-300">
                      {recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-orange-400">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {missingViews.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Vues complémentaires demandées</p>
                    <ul className="space-y-1 text-sm text-slate-300">
                      {missingViews.map((item, idx) => (
                        <li key={`${item.view || idx}-${idx}`} className="flex items-start gap-2">
                          <span className="text-orange-400">•</span>
                          <span>
                            {(item.view || item.section || 'Vue')} : {item.instruction || item.notes || item.action_needed || 'À compléter'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {qa && (
                  <details className="bg-slate-900/60 border border-slate-700 rounded-lg p-4">
                    <summary className="cursor-pointer text-orange-300 text-sm">Contrôle qualité</summary>
                    <pre className="mt-3 text-xs max-h-60 overflow-auto text-slate-200">
                      {JSON.stringify(qa, null, 2)}
                    </pre>
                  </details>
                )}
                {processedFrames.length > 0 && (
                  <div className="text-sm text-slate-400">
                    <p>Images analysées : {processedFrames.length}</p>
                    {processedFrames[0]?.width && processedFrames[0]?.height && (
                      <p>
                        Résolution images : {processedFrames[0].width}×{processedFrames[0].height}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-white">Détails des dommages</h2>
                <p className="text-sm text-slate-400">{displayDamages.length} entrées</p>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {displayDamages.map((damage, index) => (
                  <div
                    key={`${damage.part}-${index}`}
                    className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden shadow-md hover:border-orange-500/40 transition"
                  >
                    {damage.outlined_image_full ? (
                      <button
                        type="button"
                        onClick={() => setActiveImage(damage.outlined_image_full)}
                        className="block w-full"
                      >
                        <img
                          src={damage.outlined_image_full}
                          alt={`${damage.part} - ${damage.type}`}
                          className="w-full h-48 object-cover"
                          loading="lazy"
                        />
                      </button>
                    ) : (
                      <div className="w-full h-48 bg-slate-900 flex items-center justify-center text-slate-500 text-sm">
                        Pas d'image disponible
                      </div>
                    )}
                    <div className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold text-white leading-tight">{damage.part}</h3>
                        <span className="text-xs uppercase tracking-wide text-slate-400">
                          {damage.part_id || 'Inconnue'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs font-medium">
                        <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-200 capitalize">{damage.type}</span>
                        {damage.severity && (
                          <span className="px-2 py-1 rounded bg-slate-700 text-slate-200 capitalize">{damage.severity}</span>
                        )}
                        {damage.confidence !== undefined && (
                          <span className="px-2 py-1 rounded bg-slate-700 text-slate-200">{formatConfidence(damage.confidence)}</span>
                        )}
                      </div>
                      {damage.notes && (
                        <p className="text-xs text-slate-400">{damage.notes}</p>
                      )}
                      {damage.estimated_cost && (
                        <div className="text-sm text-slate-300 space-y-1">
                          {damage.estimated_cost.parts !== undefined && (
                            <p>Pièces : {formatCurrency(damage.estimated_cost.parts)}</p>
                          )}
                          {damage.estimated_cost.labor !== undefined && (
                            <p>Main d'œuvre : {formatCurrency(damage.estimated_cost.labor)}</p>
                          )}
                          {damage.estimated_cost.total !== undefined && (
                            <p className="font-semibold text-orange-200">
                              Total : {formatCurrency(damage.estimated_cost.total)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {outlinedImages.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-semibold text-white">Galerie des images détourées</h2>
                  <span className="text-sm text-slate-400">{outlinedImages.length} images</span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {outlinedImages.map((image) => (
                    <button
                      key={image.filename}
                      type="button"
                      className="group block bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-orange-500/40"
                      onClick={() => setActiveImage(image.url)}
                    >
                      <img
                        src={image.url}
                        alt={image.filename}
                        className="w-full h-40 object-cover group-hover:opacity-90 transition"
                        loading="lazy"
                      />
                      <div className="p-3 text-left">
                        <p className="text-sm font-medium text-white truncate">{image.filename}</p>
                        <p className="text-xs text-slate-400">{image.type || 'outlined'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {status === 'completed' && hasStructuredReport && displayDamages.length === 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-2">Rapport {reportDisplayLabel}</h2>
            <p className="text-sm text-slate-400">
              Aucune image détourée disponible pour cette inspection, mais les résultats chiffrés sont accessibles via l’API.
            </p>
          </div>
        )}

        {status === 'completed' && !hasStructuredReport && hasLegacyReport && (
          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Rapport d'Inspection</h2>
              
              {inspection.total_cost && (
                <div className="mb-4 p-4 bg-orange-500/10 border border-orange-500/40 rounded-lg">
                  <p className="text-2xl font-bold text-orange-200">
                    Coût total estimé: {inspection.total_cost.toFixed(2)} €
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {Object.entries(inspection.report).map(([key, value]) => (
                  <EditableField
                    key={key}
                    field_id={key}
                    field={value.field}
                    value={value.value}
                    confidence={value.confidence}
                    cost={value.cost}
                    onUpdate={handleFieldUpdate}
                  />
                ))}
              </div>
            </div>

            {inspection.missing_views && inspection.missing_views.length > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/40 rounded-lg p-6">
                <h3 className="text-lg font-bold text-orange-200 mb-3">
                  Vues manquantes recommandées
                </h3>
                <ul className="space-y-2 text-slate-200">
                  {inspection.missing_views.map((view, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="text-orange-400">•</span>
                      <span>
                        {view.view}: {view.action_needed}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {status === 'completed' && !hasStructuredReport && !hasLegacyReport && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <p className="text-slate-300">Aucun rapport exploitable n’a été fourni par le backend.</p>
          </div>
        )}
      </div>

      <ImageModal src={activeImage} alt="Image détourée" onClose={() => setActiveImage(null)} />
    </Layout>
  );
};

export default InspectionDetail;



